import React from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import type { TaskSignatureResponse } from '../../services/api/types.gen';

interface SignatureCardProps {
  signature: TaskSignatureResponse;
  /** Optional emphasis style (e.g. inside a highlighted banner). */
  tone?: 'default' | 'amber';
}

function formatTimestamp(iso: string): string {
  const dt = new Date(iso);
  // Fixed UTC so every viewer sees the same instant; wording is en-US for readability.
  const formatted = dt.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${formatted} UTC`;
}

/**
 * Renders an "Electronically Signed By" block for a single task signature.
 *
 * Intentionally mirrors what goes into the CMS-485 PDF so on-screen and
 * printed rendering agree: printed name + credentials, timestamp (UTC), and
 * truncated signature id as provenance.
 */
export const SignatureCard: React.FC<SignatureCardProps> = ({
  signature,
  tone = 'default',
}) => {
  const metadata =
    (signature.signature_metadata as
      | Record<string, unknown>
      | null
      | undefined) ?? {};
  const displayName =
    (metadata.signer_display_name as string | undefined) ||
    (metadata.user_name as string | undefined) ||
    (metadata.user_email as string | undefined) ||
    'Unknown signer';
  const credentials = metadata.signer_credentials as string | undefined | null;
  const role = metadata.signer_role as string | undefined;
  const invalidated = !!signature.invalidated_at;
  const hasIntegrity = !!signature.content_hash_hex;

  const nameLine = credentials ? `${displayName}, ${credentials}` : displayName;

  const isAmber = tone === 'amber';
  const container = isAmber
    ? 'bg-amber-50/60 border-amber-200'
    : 'bg-muted/30 border-border/70';
  const subtle = isAmber ? 'text-amber-700' : 'text-muted-foreground';
  const strong = isAmber ? 'text-amber-900' : 'text-foreground';

  return (
    <div className={`rounded-md border ${container} px-3 py-2.5`}>
      <div className='flex items-start gap-2'>
        {invalidated ? (
          <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-destructive' />
        ) : (
          <CheckCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600' />
        )}
        <div className='flex-1 min-w-0'>
          <div className={`text-[11px] uppercase tracking-wide ${subtle}`}>
            Electronically Signed By
            {role ? ` · ${role.replace(/_/g, ' ')}` : ''}
          </div>
          <div
            className={`text-sm font-medium ${strong} ${
              invalidated ? 'line-through' : ''
            }`}
          >
            {nameLine}
          </div>
          <div className={`text-xs ${subtle}`}>
            {formatTimestamp(signature.signed_at)}
          </div>
          <div className={`text-[11px] font-mono ${subtle}`}>
            Signature ID: {signature.id.slice(0, 8)}
            {hasIntegrity ? ' · hmac-sha256' : ''}
          </div>
          {invalidated ? (
            <div className='mt-1 text-[11px] text-destructive'>
              Invalidated
              {signature.invalidation_reason
                ? ` — ${signature.invalidation_reason}`
                : ''}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
